// @ts-strict-ignore
import { Component, DestroyRef, ElementRef, HostBinding, OnDestroy, OnInit, Signal, viewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs/internal/Subject';
import { PermissionsNavigationModel } from './permissions-navigation.model';
import { PermissionsNavigationService } from './service/permissions-navigation.service';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectHeadVO, ProjectVO } from '@project/model/vo/project.vo';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { UserModel } from '@user/model/user.model';
import { FriendSharedInfo } from '@project/components/permissions/friend-shared-info';
import { Dictionary } from 'typescript-collections';
import { SharedUserRightVO } from '@user/model/vo/shared-user-right.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { AlertButton } from '@shared/alert/alertButton';
import { AlertHelper } from '@shared/alert/alert.helper';
import { StringUtils } from '@shared/util/string-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { UserSettings } from '@user/model/user-settings';
import { ToastrService } from 'ngx-toastr';
import { ProjectModel } from '../../model/project.model';
import { ProjectGroupLoadingService } from '../project/service/project-group-loading.service';
import { SharedObjectInfoDataService } from '../project/service/shared-object-info-data.service';
import { FriendListService } from './service/friend-list.service';
import { UserComponentPresenter } from '@user/components/user-component.presenter';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectComponentPresenter } from '../project-component.presenter';
import { SvgIconName } from '@shared/svg-icon/svg-icon-name';
import { ProjectHistoryService } from '../../history/project-history.service';

/**
 * Component to set the user permissions of a project
 */
@Component({
  standalone: false,
  selector: 'app-permissions',
  templateUrl: './permissions.component.html',
})
export class PermissionsComponent implements OnInit, OnDestroy {
  public readonly PROJECT_FOLDER_TYPE: number = ProjectGroupUtils.PROJECT;

  private searchProject: Signal<ElementRef> = viewChild('searchProject');
  private searchUser: Signal<ElementRef> = viewChild('searchUser');

  @HostBinding(`class.d-flex`) displayFlexClass = true;
  @HostBinding(`class.flex-column`) flexDirectionClass = true;
  @HostBinding(`style.min-height.px`) minHeight = 330;

  public onClose: Subject<boolean>;

  public model: PermissionsNavigationModel = new PermissionsNavigationModel();
  public service: PermissionsNavigationService;
  public viewItems: (ProjectGroupVO | ProjectHeadVO)[];

  constructor(
    private _bsModalRef: BsModalRef,
    private userSettings: UserSettings,
    private userModel: UserModel,
    private alertHelper: AlertHelper,
    private localeResourceManager: LocaleResourceManager,
    private projectGroupLoadingService: ProjectGroupLoadingService,
    private projectModel: ProjectModel,
    private friendListService: FriendListService,
    private sharedObjectInfoDataService: SharedObjectInfoDataService,
    private toastrService: ToastrService,
    private _userComponentPresenter: UserComponentPresenter,
    private destroyRef: DestroyRef,
    private projectComponentPresenter: ProjectComponentPresenter,
    private projectHistoryService: ProjectHistoryService,
  ) {}

  public get userID(): number {
    return this.userModel?.user?.id;
  }

  public ngOnInit(): void {
    this.onClose = new Subject();

    this.service = new PermissionsNavigationService(
      this.model,
      this.userSettings,
      this.userModel,
      this.projectGroupLoadingService,
      this.projectModel,
      this.alertHelper,
      this.friendListService,
      this.sharedObjectInfoDataService,
      this.toastrService,
      this.localeResourceManager
    );

    this.service.initSubscribtions();
    this.service.loadInitialData();

    this.model.currentChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((group: ProjectGroupVO) => {
      if (!group) {
        this.viewItems = [];
        return;
      }
      const viewItems: (ProjectGroupVO | ProjectHeadVO)[] = [];
      group.groups.forEach((g) => {
        viewItems.push(g);
        if (g.groupType === ProjectGroupUtils.PROJECT && this.model.collapsedProjects.indexOf(g.id) !== -1) {
          for (let project of g.projects) {
            project.projectGroup = g;
            viewItems.push(project);
          }
        }
      });
      this.viewItems = [...viewItems, ...group.projects];
      this.service.updateRightsBySelectedNode();
    });
  }

  public ngOnDestroy(): void {
    this.service.destroySubscribtions();
    this.service.saveLastSelectedShareProjectGroup();
  }

  public openProjectGroup(projectGroup: ProjectGroupVO): void {
    this.service.loadProjectGroup(projectGroup, true).then(() => {});
  }

  public navigateInChain(projectGroup: ProjectGroupVO): void {
    if (projectGroup) {
      this.service.navigateInChain(projectGroup);
    }
  }

  public selectItem(item: ProjectHeadVO | ProjectGroupVO): void {
    this.service.selectItem(item);
  }

  public selectProjectGroup(projectGroup: ProjectGroupVO): void {
    this.service.loadProjectGroup(projectGroup, false).then(() => {
      this.selectItem(projectGroup);
    });
  }

  public collapse(projectGroup: ProjectGroupVO): void {
    if (
      !(
        ProjectGroupUtils.isConfigurationGroup(projectGroup) &&
        this.model.currentSelectedItem &&
        this.model.currentSelectedItem instanceof ProjectGroupVO &&
        projectGroup.id === this.model.currentSelectedItem.id
      )
    ) {
      return;
    }
    if (this.model.collapsedProjects.indexOf(projectGroup.id) !== -1) {
      this.model.collapsedProjects.splice(this.model.collapsedProjects.indexOf(projectGroup.id), 1);
    } else {
      this.model.collapsedProjects.push(projectGroup.id);
    }
    this.openProjectGroup(projectGroup);
  }

  public async performUndo(): Promise<void> {
    await this.service.performUndo();
  }

  public async performRedo(): Promise<void> {
    await this.service.performRedo();
  }

  public readCheckBoxChanged(friendSharedInfo: FriendSharedInfo) {
    this.service.readCheckBoxChanged(friendSharedInfo);
  }

  public editCheckBoxChanged(friendSharedInfo: FriendSharedInfo) {
    this.service.editCheckBoxChanged(friendSharedInfo);
  }

  public selectAllReadCheckBoxChanged(): void {
    this.service.selectAllReadChanged();
  }

  public selectAllEditCheckBoxChanged(): void {
    this.service.selectAllEditChanged();
  }

  public removeUserFromFriendList(friend: FriendSharedInfo): void {
    this.service.removeUserFromFriendList(friend);
  }

  public performSearchProject(): Promise<void> {
    return this.service.performSearchProject(this.searchProject()?.nativeElement.value);
  }

  public async performSearchUser(): Promise<void> {
    await this.service.performSearchUser(this.searchUser()?.nativeElement.value);
  }

  public close(): void {
    this.onClose.next(undefined);
    this._bsModalRef.hide();
  }

  public onConfirm(): void {
    this.service.saveChangedSharedRights(true);
  }

  public onCancel(): void {
    if (this.service.friendListContentPresenter.isUserRightsChanged()) {
      this.promptToUpdateUnsavedSharedUserRights();
    } else {
      this.close();
    }
  }

  public getFinalizationStateIcon(project: ProjectVO): string {
    return ProjectUtils.getFinalizationStateIcon(project);
  }

  public getFinalizationStateTooltip(project: ProjectVO): string | undefined {
    return this.projectHistoryService.getFinalizationStateText(project);
  }

  private findGroupById(groupId: number, groups: ProjectGroupVO[]): ProjectGroupVO {
    for (const group of groups) {
      if (group.id === groupId) {
        return group;
      }
      if (ArrayUtils.isNotEmpty(group.groups)) {
        const projectGroup: ProjectGroupVO = this.findGroupById(groupId, group.groups);
        if (projectGroup) {
          return projectGroup;
        }
      }
    }
    return null;
  }

  private findProjectById(projectId: number, groups: ProjectGroupVO[]): ProjectHeadVO {
    for (const group of groups) {
      if (ArrayUtils.isNotEmpty(group.projects)) {
        for (const projectHead of group.projects) {
          if (projectHead.id === projectId) {
            return projectHead;
          }
        }
      }
      if (ArrayUtils.isNotEmpty(group.groups)) {
        const projectHead: ProjectHeadVO = this.findProjectById(projectId, group.groups);
        if (projectHead) {
          return projectHead;
        }
      }
    }
    return null;
  }

  public openModalPermissionsUserDialog(friend: FriendSharedInfo): void {
    const preselectedProjectGroup: ProjectGroupVO = this.model.currentSelectedItem
      ? this.model.currentSelectedItem instanceof ProjectGroupVO
        ? this.model.currentSelectedItem
        : this.model.currentSelectedItem.projectGroup
      : null;
    const changedGroupIds: number[] = [];
    const changedRootIds: number[] = [];
    for (const id of this.service.friendListContentPresenter.changedRightsForGroupIds) {
      const group: ProjectGroupVO = this.findGroupById(id, this.model.rootProjectGroup.groups);
      if (group) {
        this.updateLocallyChangedIds(group, changedGroupIds, changedRootIds, true);
      }
    }

    const changedRightsForProjectIds: number[] = this.service.friendListContentPresenter.changedRightsForProjectIds;
    for (const id of changedRightsForProjectIds) {
      const projectNode: ProjectHeadVO = this.findProjectById(id, this.model.rootProjectGroup.groups);
      if (projectNode) {
        this.updateLocallyChangedIds(projectNode, changedGroupIds, changedRootIds, false);
      }
    }
    const groupIdToSharedInfos: Dictionary<number, SharedUserRightVO> = this.service.friendListContentPresenter.getLocalSharedRightsForUser(
      friend.userId,
      changedGroupIds,
      new ProjectGroupVO()
    );
    const projectIdToSharedInfos: Dictionary<number, SharedUserRightVO> = this.service.friendListContentPresenter.getLocalSharedRightsForUser(
      friend.userId,
      changedRightsForProjectIds,
      new ProjectVO()
    );

    // Collect information for the child project groups and projects whose rights will be checked at server side.
    const groupIdToChildGroupIdsForFiltering: Dictionary<number, number[]> = new Dictionary<number, number[]>();
    const groupIdToProjectIdsForFiltering: Dictionary<number, number[]> = new Dictionary<number, number[]>();
    for (const groupId of groupIdToSharedInfos.keys()) {
      this.collectNodeIdsForSharedRightFiltering(
        groupId,
        groupIdToChildGroupIdsForFiltering,
        groupIdToProjectIdsForFiltering,
        groupIdToSharedInfos,
        projectIdToSharedInfos
      );
    }

    this._userComponentPresenter.presentPermissionsUserComponent({
      preselectedFriendSharedInfo: friend,
      preselectedProjectGroupId: preselectedProjectGroup ? preselectedProjectGroup.id : NaN,
      changedRootIds,
      groupIdToSharedInfos,
      projectIdToSharedInfos,
      groupIdToChildGroupIdsForFiltering,
      groupIdToProjectIdsForFiltering,
      ownerProjectGroups: this.model.rootProjectGroup.groups,
    });
  }

  private updateLocallyChangedIds(
    group: ProjectGroupVO | ProjectHeadVO,
    changedNodeIds: number[],
    changedRootIds: number[],
    updateChangedNodeIdsByChecked: boolean
  ): void {
    const parentNodes: (ProjectGroupVO | ProjectHeadVO)[] = this.service.friendListContentPresenter.getParentDependentNodes(group);
    if (updateChangedNodeIdsByChecked) {
      parentNodes.push(group);
    }

    for (const parentNode of parentNodes) {
      const nodeId: number = parentNode.id;
      if (changedNodeIds.indexOf(nodeId) < 0) {
        changedNodeIds.push(nodeId);
      }
      if (parentNode instanceof ProjectGroupVO && !parentNode.parentGroup && changedRootIds.indexOf(nodeId) < 0) {
        changedRootIds.push(nodeId);
      }
    }
  }

  private collectNodeIdsForSharedRightFiltering(
    projectGroupId: number,
    groupIdToChildGroupIds: Dictionary<number, number[]>,
    groupIdToProjectIds: Dictionary<number, number[]>,
    groupIdToSharedInfos: Dictionary<number, SharedUserRightVO>,
    projectIdToSharedInfos: Dictionary<number, SharedUserRightVO>
  ): void {
    const projectGroup = this.findGroupById(projectGroupId, this.model.rootProjectGroup.groups);
    const sharedUserRightVO: SharedUserRightVO = groupIdToSharedInfos.getValue(projectGroupId);
    if (!projectGroup || !sharedUserRightVO || sharedUserRightVO.updateChildrenAvailable) {
      return;
    }
    const children = projectGroup instanceof ProjectGroupVO ? [...projectGroup.groups, ...projectGroup.projects] : [];
    const childGroupIds: number[] = [];
    const childProjectIds: number[] = [];
    for (const childNode of children) {
      if (childNode instanceof ProjectGroupVO) {
        childGroupIds.push(childNode.id);
      } else if (childNode instanceof ProjectHeadVO) {
        childProjectIds.push(childNode.id);
      }
    }
    const projectGroupIds: number[] = this.getChangedNodesIds(childGroupIds, groupIdToSharedInfos);
    if (!ArrayUtils.isEmpty(projectGroupIds)) {
      groupIdToChildGroupIds.setValue(projectGroupId, projectGroupIds);
    }

    const projectIds: number[] = this.getChangedNodesIds(childProjectIds, projectIdToSharedInfos);
    if (!ArrayUtils.isEmpty(projectIds)) {
      groupIdToProjectIds.setValue(projectGroupId, projectIds);
    }
  }

  private getChangedNodesIds(nodeIds: number[], idsToSharedInfos: Dictionary<number, SharedUserRightVO>): number[] {
    const changedNodeIds: number[] = [];
    for (const nodeId of nodeIds) {
      if (!idsToSharedInfos.containsKey(nodeId)) {
        changedNodeIds.push(nodeId);
      }
    }
    return changedNodeIds;
  }

  private promptToUpdateUnsavedSharedUserRights(): void {
    this.alertHelper
      .showAlert(
        'shareProjectPopup.alert.title.user_shared_rights_not_saved',
        'shareProjectPopup.alert.message.user_shared_rights_not_saved',
        null,
        [AlertButton.YES, AlertButton.NO, AlertButton.CANCEL],
        "error"
      )
      .then((answer) => {
        if (answer === AlertButton.YES) {
          this.service.saveChangedSharedRights(false);
          this.close();
        } else if (answer === AlertButton.NO) {
          this.service.friendListContentPresenter.removeModifiedSharedRightsFromFriendList();
          this.close();
        }
      });
  }

  public openProjectVersionHistoryComponent(project: ProjectVO): void {
    this.projectComponentPresenter.openProjectVersionHistoryComponent(project).then((projectLoaded) => {
      if (projectLoaded) {
        this.onCancel();
      }
    });
  }

  public isSearchedItem(item: ProjectGroupVO | ProjectHeadVO): boolean {
    const searchString: string = this.model.searchText;
    if (!searchString || !item || !this.model.searchMode) {
      return false;
    }
    if (item instanceof ProjectHeadVO) {
      return (
        StringUtils.containsString(item.name, searchString) ||
        StringUtils.containsString(item.articleNumber, searchString) ||
        StringUtils.containsString(item.articleNumberLong.reducedArticleNumberLong, searchString) ||
        StringUtils.containsString(item.drawingNumber, searchString)
      );
    } else {
      return ProjectGroupUtils.isConfigurationGroupType(item.groupType) && StringUtils.containsString(item.groupName, searchString);
    }
  }

  public openHELPLink(): void {
    window.open(this.localeResourceManager.getValueByKey(`helplink.grantpermission`), `_blank`);
  }

  public getEntryIcon(entry: ProjectGroupVO): SvgIconName {
    return ProjectGroupUtils.getProjectGroupIcon(entry, this.userModel.user);
  }
}
